// /ask — "Ask your videos": one question across every processed video in the Workspace. askLibrary picks
// the relevant videos and answers with citations like [2 · 4:15], linked here to that workspace.
// The conversation is users/{uid}/libraryChat/main (backend-written).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet';

import { auth, db } from '../firebase';
import AppTransition from '../components/appComponents/AppTransition';
import PlanPurchasePage from '../myPlans/PlanPurchasePage';
import usePlan, { isProPlan } from '../myPlans/usePlan';
import useOnboarding from '../myWorkspace/onboarding/useOnboarding';
import { newTurnId } from '../myWorkspace/workspaceComponents/chat/chatModel';
import bumpMark from '../assets/images/logo/bumpups-green-logo.png';
import { track } from '../analytics/track';
import { libraryLimit, questionsUsed, turnsOf, linkCitations, withStale } from './askModel';
import '../myWorkspace/workspaceComponents/chat/workspaceChat.css';
import './askPage.css';

const SUGGESTIONS = {
  study: ['Make me a study guide across my lectures', 'Which video explains the hardest topic best?', 'Quiz me on everything I’ve added'],
  teach: ['Which lessons used group work, and when?', 'What questions did students ask across my lessons?', 'Summarize what each lesson covered'],
  coach: ['What drills have we covered across sessions?', 'Where do we talk about defending corners?', 'Summarize the key coaching points so far'],
  create: ['Which moments would make the best short clips?', 'What topics come up most across my videos?', 'Draft a newsletter from my latest videos'],
  work: ['What decisions were made across my meetings?', 'List open action items and who owns them', 'Which meeting discussed the budget?'],
};
const DEFAULT_SUGGESTIONS = ['What are the main ideas across my videos?', 'Which video should I rewatch first, and why?', 'Summarize everything I’ve added this month'];

const CALL_TIMEOUT_MS = 300 * 1000;

export default function AskPage() {
  const uid = auth.currentUser?.uid;
  const navigate = useNavigate();
  const plan = usePlan();
  const [onboarding] = useOnboarding();
  const [chat, setChat] = useState(undefined);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(null); // { id, question } until the server records it
  const [error, setError] = useState('');
  const [plansSource, setPlansSource] = useState(null);
  const bottomRef = useRef(null);
  const ask = useMemo(() => httpsCallable(getFunctions(), 'askLibrary', { timeout: CALL_TIMEOUT_MS }), []);

  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(doc(db, 'users', uid, 'libraryChat', 'main'), snap => setChat(snap.exists() ? snap.data() : null), () => setChat(null));
  }, [uid]);

  const turns = withStale(turnsOf(chat));
  const shown = pending && !turns.some(t => t.id === pending.id) ? [...turns, { id: pending.id, question: pending.question, status: 'pending' }] : turns;
  const busy = shown.some(t => t.status === 'pending');
  const limit = libraryLimit(plan);
  const used = questionsUsed(chat);
  const left = Math.max(0, limit - used);
  const isPro = isProPlan(plan);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [shown.length, busy]);

  const send = async text => {
    const question = (text ?? draft).trim();
    if (!question || busy || left <= 0) return;
    const id = newTurnId();
    setPending({ id, question });
    setDraft('');
    setError('');
    track('library_asked', { n: used + 1, isPro });
    try {
      let timeZone = '';
      try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* old browser: UTC */ }
      await ask({ question, turnId: id, timeZone });
    } catch (e) {
      setPending(null);
      setDraft(question);
      setError(/resource-exhausted|failed-precondition|invalid-argument/.test(e?.code || '') && e.message
        ? e.message : 'Your question couldn’t be sent. Check your connection and try again.');
    }
  };

  const suggestions = SUGGESTIONS[onboarding?.useCase] || DEFAULT_SUGGESTIONS;
  const markdownComponents = {
    a: ({ node, href, children, title }) => (href || '').startsWith('/workspace/')
      ? <Link to={href} className="ask-cite" title={title}>{children}</Link>
      : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
  };

  return (
    <div className="ask-page">
      <Helmet><title>Bumpups | Ask your videos</title></Helmet>
      <AppTransition>
        <div className="ask-column">
          <header className="ask-head">
            <h1 className="ask-title">Ask your videos</h1>
            <p className="ask-sub">One question across every video in your Workspace. Answers point to the video and the moment.</p>
          </header>

          <div className="ask-thread">
            {chat === undefined ? (
              <div className="wsc-loading" aria-label="Loading"><span /><span /><span /></div>
            ) : shown.length === 0 ? (
              <div className="ask-empty">
                <p className="ask-empty-title">Try one of these</p>
                <div className="wsc-suggestions ask-suggestions">
                  {suggestions.map(s => (
                    <button key={s} type="button" className="wsc-suggestion" onClick={() => send(s)} disabled={left <= 0}>{s}</button>
                  ))}
                </div>
              </div>
            ) : shown.map(t => (
              <div key={t.id} className="ask-turn">
                <div className="ask-q">{t.question}</div>
                <div className="ask-a">
                  <img src={bumpMark} alt="" className="ask-mark" />
                  <div className="ask-a-body">
                    {t.status === 'pending' ? (
                      <p className="ask-wait"><span className="ask-dots"><i /><i /><i /></span> Looking through your videos…</p>
                    ) : t.status === 'error' ? (
                      <p className="ask-failed"><FontAwesomeIcon icon={faCircleExclamation} /> {t.answer}</p>
                    ) : (
                      <>
                        <ReactMarkdown className="wsc-markdown" remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {linkCitations(t.answer, t.sources)}
                        </ReactMarkdown>
                        {(t.sources || []).length > 0 && (
                          <div className="ask-sources">
                            {t.sources.map(s => (
                              <button key={s.n} type="button" className="ask-source" onClick={() => navigate(`/workspace/${s.content_id.replace(/^workspace-/, '')}`)} title={s.title}>
                                <span className="ask-source-n">{s.n}</span>
                                {s.thumbnail && /^https:/.test(s.thumbnail) && <img src={s.thumbnail} alt="" className="ask-source-thumb" />}
                                <span className="ask-source-title">{s.title}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="ask-bottom">
            {error && <div className="wsc-send-error" role="alert"><FontAwesomeIcon icon={faCircleExclamation} /> {error}</div>}
            <form className="ask-composer" onSubmit={e => { e.preventDefault(); send(); }}>
              <textarea
                className="ask-input"
                value={draft}
                onChange={e => setDraft(e.target.value.slice(0, 1000))}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={left > 0 ? 'Ask anything about your videos' : 'No questions left this month'}
                rows={1}
                disabled={left <= 0}
                aria-label="Ask your videos"
              />
              <button type="submit" className="ask-send" disabled={!draft.trim() || busy || left <= 0} aria-label="Ask">
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
            </form>
            {plan && (
              <p className="ask-allowance">
                {left} of {limit} questions left this month
                {!isPro && (
                  <> · <button type="button" className="ask-upgrade" onClick={() => setPlansSource('library_limit')}>Pro includes 100</button></>
                )}
              </p>
            )}
          </div>
        </div>
      </AppTransition>
      {plansSource && <PlanPurchasePage source={plansSource} onClose={() => setPlansSource(null)} />}
    </div>
  );
}
